import {Alert, DataStatus, IconButton} from 'platform/components';
import {Box, Center, HStack, Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {ListContextParametersApiResponse} from '@dms/api/core';
import type {FinancingEndingPreviewResponseBody} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {EmailWindow} from '@dms/shared';

import type {Nullish} from 'shared/web';

import {getFinancingEndingReasonLabel} from '../../utils/getFinancingEndingReasonLabel';

const PREVIEW_HEIGHT = 100;

export type FinancingEndingPreviewNavigatorProps = {
  index: number;
  count: number;
  recipientName: string | Nullish;
  recipientEmail: string | Nullish;
  sender: string | Nullish;
  /** The placeholder catalogue that names the missing values; the parent loads it once. */
  parameters: ListContextParametersApiResponse;
  isLoading: boolean;
  preview: FinancingEndingPreviewResponseBody | Nullish;
  skipReasonCode: string | Nullish;
  /** A refusal that is not about the recipient, shown as plain text. */
  errorMessage?: string | Nullish;
  placeholder: string | Nullish;
  onPrevious: () => void;
  onNext: () => void;
};

export function FinancingEndingPreviewNavigator(props: FinancingEndingPreviewNavigatorProps) {
  // A code the catalogue does not know, or a catalogue not loaded yet, is shown as the code.
  const getPlaceholderLabel = (code: string) =>
    props.parameters.find((parameter) => parameter.code === code)?.label ?? code;

  return (
    <VStack spacing={3} height="100%" data-testid={testIds.businessCase.financingEnding('preview')}>
      <HStack spacing={2} align="center" justify="space-between">
        <Text size="small" color="secondary">
          {i18n.t('entity.businessCase.financingEnding.preview.position', {
            index: props.index + 1,
            count: props.count,
          })}
        </Text>
        <HStack
          spacing={2}
          align="center"
          data-testid={testIds.businessCase.financingEnding('preview-recipient')}
        >
          <IconButton
            icon="navigation/chevron_left"
            isDisabled={props.index <= 0}
            onClick={props.onPrevious}
            aria-label={i18n.t('entity.businessCase.financingEnding.preview.previous')}
            data-testid={testIds.businessCase.financingEnding('preview-previous')}
          />
          <Text size="small" alternative>
            {props.recipientName}
          </Text>
          <Text size="small" color="tertiary">
            {props.recipientEmail}
          </Text>
          <IconButton
            icon="navigation/chevron_right"
            isDisabled={props.index >= props.count - 1}
            onClick={props.onNext}
            aria-label={i18n.t('entity.businessCase.financingEnding.preview.next')}
            data-testid={testIds.businessCase.financingEnding('preview-next')}
          />
        </HStack>
      </HStack>
      {isNotNil(props.placeholder) ? (
        <Center grow={1} data-testid={testIds.businessCase.financingEnding('preview-placeholder')}>
          <Text size="small" color="tertiary">
            {props.placeholder}
          </Text>
        </Center>
      ) : (
        <DataStatus isLoading={props.isLoading} minHeight={PREVIEW_HEIGHT} grow={1}>
          <Show when={isNotNilOrEmpty(props.skipReasonCode) ? props.skipReasonCode : null}>
            {(skipReasonCode) => (
              <Alert
                variant="warning"
                message={getFinancingEndingReasonLabel(skipReasonCode)}
                data-testid={testIds.businessCase.financingEnding('preview-skipReason')}
              />
            )}
          </Show>
          <Show when={isNotNilOrEmpty(props.errorMessage) ? props.errorMessage : null}>
            {(errorMessage) => (
              <Alert
                variant="error"
                message={errorMessage}
                data-testid={testIds.businessCase.financingEnding('preview-error')}
              />
            )}
          </Show>
          <Show when={props.preview}>
            {(preview) => (
              <VStack spacing={2} grow={1}>
                <Show when={isNotNilOrEmpty(preview.missingPlaceholders)}>
                  <Alert
                    variant="warning"
                    message={i18n.t('entity.businessCase.financingEnding.preview.missingValues', {
                      names: preview.missingPlaceholders.map(getPlaceholderLabel).join(', '),
                    })}
                    data-testid={testIds.businessCase.financingEnding('preview-missingValues')}
                  />
                </Show>
                <Box
                  padding={3}
                  flexGrow={1}
                  backgroundColor="palettes.neutral.10.100"
                  borderRadius="medium"
                >
                  <VStack spacing={3} height="100%">
                    <Box padding={4} backgroundColor="general.white" borderRadius="medium">
                      <VStack
                        spacing={1}
                        data-testid={testIds.businessCase.financingEnding('preview-meta')}
                      >
                        <Show when={isNotNilOrEmpty(props.sender) ? props.sender : null}>
                          {(sender) => (
                            <Text size="xSmall" color="secondary">
                              {i18n.t('entity.businessCase.financingEnding.preview.senderLine', {
                                sender,
                              })}
                            </Text>
                          )}
                        </Show>
                        <Text size="xSmall" color="secondary">
                          {i18n.t('entity.businessCase.financingEnding.preview.recipientLine', {
                            recipient: preview.recipientEmail,
                          })}
                        </Text>
                        <Text size="small" alternative>
                          {preview.subject}
                        </Text>
                      </VStack>
                    </Box>
                    <Box flexGrow={1} minHeight={PREVIEW_HEIGHT}>
                      <EmailWindow
                        htmlData={preview.bodyHtml}
                        data-testid={testIds.businessCase.financingEnding('preview-email')}
                      />
                    </Box>
                  </VStack>
                </Box>
              </VStack>
            )}
          </Show>
        </DataStatus>
      )}
    </VStack>
  );
}
